import { BuyerUI } from '@choco-admin/types';
import { useCallback } from 'react';

import { useBuyersPageCreateBuyerMutation } from '../../../generated';
import { mapToBuyerUI } from '../utils';

export type CaptureExceptionFunction = (error: Error) => void;

export type CreateBuyerInput = {
  name: string;
  displayName?: string;
  fullAddress?: string;
  addressCity?: string;
  addressCountry?: string;
  addressLatitude?: number;
  addressLongitude?: number;
  addressUTCOffset?: number;
  addressGooglePlaceId?: string;
  addressGoogleAddressId?: string;
  addressSource?: string;
  deliveryComment?: string;
  isTest: boolean;
};

export type UseCreateBuyerResponse = {
  createBuyer: (input: CreateBuyerInput) => Promise<BuyerUI>;
  isLoading: boolean;
};

export type UseCreateBuyerConfig = {
  captureException?: CaptureExceptionFunction;
};

export const useCreateBuyer = (
  config: UseCreateBuyerConfig
): UseCreateBuyerResponse => {
  const { captureException } = config;

  const [createBuyerMutation, { loading }] = useBuyersPageCreateBuyerMutation();

  const createBuyer = useCallback(
    async (input: CreateBuyerInput): Promise<BuyerUI> => {
      try {
        const result = await createBuyerMutation({
          // Spelled out even though it is the client-wide default: everything
          // below depends on this mutation RESOLVING with `errors` rather than
          // rejecting (see the failure handling after the await).
          errorPolicy: 'all',
          variables: {
            buyerInput: {
              name: input.name,
              displayName: input.displayName,
              fullAddress: input.fullAddress || '',
              city: input.addressCity,
              country: input.addressCountry,
              latitude: input.addressLatitude,
              longitude: input.addressLongitude,
              utcOffset: input.addressUTCOffset,
              googlePlaceId: input.addressGooglePlaceId,
              googlePlaceAddressId: input.addressGoogleAddressId,
              addressSource:
                input.addressSource === 'google' ? 'google' : 'custom',
              deliveryComment: input.deliveryComment,
              isTest: input.isTest,
            },
          },
          awaitRefetchQueries: true,
        });

        const createdBuyer = result.data?.buyerCreate;

        // The payload decides, not `result.errors`. With `errorPolicy: 'all'` a
        // response can carry both a created buyer and errors about fields the
        // server could not resolve; that buyer exists and the caller must get
        // it. Only a missing payload is a failure — and because the mutation
        // resolved, the reason is in `result.errors`, never in a rejection.
        if (!createdBuyer) {
          const reason = result.errors
            ?.map((graphQLError) => graphQLError.message)
            .join('; ');
          throw new Error(reason || 'Buyer creation returned no buyer');
        }

        // Map to generic BuyerUI type using shared mapper
        return mapToBuyerUI(createdBuyer);
      } catch (error) {
        const failure =
          error instanceof Error ? error : new Error('Buyer creation failed');
        captureException?.(failure);
        throw failure;
      }
    },
    [createBuyerMutation, captureException]
  );

  return {
    createBuyer,
    isLoading: loading,
  };
};
